import { useState, useContext, useMemo } from 'react';
import Joi from 'joi';
import { Typography, Grid, MenuItem, Form, TextButton, visuallyHidden } from 'bonfire-kindling';
import { NumEmployees, Roles } from 'constants/settings';
import { useTranslation } from 'react-i18next';
import axios from 'helpers/axios';
import { ToastContext } from 'contexts';
import { VENDORS_ME } from 'constants/apis';
import { vendorSchema } from 'constants/schemas';
import { pickVendorBusinessInfoFields, useTranslatedVendorSchema } from 'helpers/vendor';
import { createFormValidator } from 'helpers/validation';
import WelcomeFooter from '../WelcomeFooter';

const PAPER_MAX_WIDTH = '1000px';

const vendorTranslationFields = [
  { name: 'vendorContactFirstName', translation: 'welcome.vendor_contact_first_name_label' },
  { name: 'vendorContactLastName', translation: 'welcome.vendor_contact_last_name_label' },
  { name: 'vendorContactEmail', translation: 'welcome.vendor_contact_email_label' },
  { name: 'role', translation: 'welcome.business_info.your_role' },
  { name: 'numEmployees', translation: 'welcome.business_info.num_employees' },
];

const vendorFields = vendorTranslationFields.map(({ name }) => name);

const schema = Joi.object({
  vendorContactFirstName: vendorSchema.extract('vendorContactFirstName'),
  vendorContactLastName: vendorSchema.extract('vendorContactLastName'),
  vendorContactEmail: vendorSchema.extract('vendorContactEmail'),
  role: vendorSchema.extract('role'),
  numEmployees: vendorSchema.extract('numEmployees'),
});

const Welcome2 = ({ decrementIndex, values, onSubmit: _onSubmit, onChange: _onChange }) => {
  const { t } = useTranslation();
  const [isSaving, setIsSaving] = useState(false);
  const translatedSchema = useTranslatedVendorSchema(vendorTranslationFields, schema);
  const validate = useMemo(() => createFormValidator(translatedSchema), [translatedSchema]);
  const toastContext = useContext(ToastContext);

  const onChange = e => {
    const { name, value } = e.target;
    _onChange({ [name]: value });
  };

  const onSubmit = async () => {
    const payload = pickVendorBusinessInfoFields(values, vendorFields);

    const errors = validate(payload);
    if (errors) {
      return errors;
    }

    setIsSaving(true);
    try {
      await axios.patch(VENDORS_ME, payload);
      _onSubmit(payload);
      toastContext.triggerToast({ variant: 'success' });
    } catch (err) {
      toastContext.triggerToast({});
      setIsSaving(false);
    }
  };

  return (
    <div>
      <Typography component="h1" sx={visuallyHidden}>
        {t('welcome.business_info.title')}
      </Typography>
      <Typography
        variant="h4"
        component="h2"
        sx={{
          marginBottom: theme => theme.spacing(3),
        }}
      >
        {t('welcome.business_info.intro')}
      </Typography>
      <Typography variant="body2" sx={{ my: theme => theme.spacing(3) }}>
        {t('welcome.change_answers')}
      </Typography>
      <Form onSubmit={onSubmit}>
        {({ handleSubmit }) => (
          <form onSubmit={handleSubmit}>
            <Grid container columnSpacing={3} sx={{ mb: 2 }}>
              <Grid item xs={12}>
                <Form.SelectField
                  label={t('welcome.business_info.num_employees')}
                  name="numEmployees"
                  margin="dense"
                  value={values.numEmployees}
                  onChange={onChange}
                  fullWidth
                  required
                >
                  {NumEmployees.map(({ value, label }) => (
                    <MenuItem key={value} value={value} label={label} />
                  ))}
                </Form.SelectField>
              </Grid>
              <Grid item xs={12}>
                <Form.SelectField
                  label={t('welcome.business_info.your_role')}
                  name="role"
                  margin="dense"
                  value={values.role}
                  onChange={onChange}
                  fullWidth
                  required
                >
                  {Roles.map(({ value, label }) => (
                    <MenuItem key={value} value={value} label={label} />
                  ))}
                </Form.SelectField>
              </Grid>
              <Grid item xs={6}>
                <Form.TextField
                  label={t('welcome.vendor_contact_first_name_label')}
                  fullWidth
                  margin="dense"
                  name="vendorContactFirstName"
                  onChange={onChange}
                  value={values.vendorContactFirstName}
                />
              </Grid>
              <Grid item xs={6}>
                <Form.TextField
                  label={t('welcome.vendor_contact_last_name_label')}
                  fullWidth
                  margin="dense"
                  name="vendorContactLastName"
                  onChange={onChange}
                  value={values.vendorContactLastName}
                />
              </Grid>
              <Grid item xs={12}>
                <Form.TextField
                  label={t('welcome.vendor_contact_email_label')}
                  fullWidth
                  margin="dense"
                  name="vendorContactEmail"
                  onChange={onChange}
                  type="email"
                  value={values.vendorContactEmail}
                />
              </Grid>
            </Grid>
            <WelcomeFooter stickyWidth={PAPER_MAX_WIDTH}>
              <TextButton
                variant="text"
                onClick={() => decrementIndex()}
                label={`< ${t('components.back')}`}
                sx={{ fontWeight: '600' }}
              />
              <TextButton
                type="submit"
                variant="contained"
                color="primary"
                disabled={values.numEmployees === '' || values.role === ''}
                isBusy={isSaving}
                label={t('welcome.save_next')}
              />
            </WelcomeFooter>
          </form>
        )}
      </Form>
    </div>
  );
};

export default Welcome2;
